import { useState } from 'preact/hooks'
import { websApi, type WebViewDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'

interface UsePullAgendaParams {
  web: WebViewDto
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
}

export interface AgendaComparison {
  platform: string[]
  bigMarker: string[]
}

const normalize = (items: string[] | undefined | null) =>
  (items ?? []).map(item => item.trim()).filter(item => item.length > 0)

/**
 * The Pull agenda from BigMarker button (ORB-943). BigMarker's API can't be sent an agenda, so this is
 * the only way to bring the two back in line, and it is always the PM's choice: nothing pulls
 * automatically, and nothing is merged. Reading saves nothing; Overwrite replaces the platform's agenda.
 */
export function usePullAgenda({ web, onPatchWeb }: UsePullAgendaParams) {
  const { showError, showInfo } = useToast()
  const [pulling, setPulling] = useState(false)
  const [overwriting, setOverwriting] = useState(false)
  const [comparison, setComparison] = useState<AgendaComparison | null>(null)
  const [justUpdated, setJustUpdated] = useState(false)

  const pull = async () => {
    setPulling(true)
    setJustUpdated(false)
    const res = await websApi.getBigMarkerAgenda(web.WorkspaceId, web.Id)
    setPulling(false)
    if (res.error || !res.data) {
      // A 422 (no Conference Id, conference gone, BigMarker unreachable) already gets the global
      // validation toast with the reason; a second one here would repeat it.
      if (res.status !== 422) showError('Couldn’t pull the agenda', res.error || 'BigMarker returned nothing.')
      return
    }

    const platform = normalize(web.OnlineEvent?.Agenda)
    const bigMarker = normalize(res.data.Agenda)
    // An empty BigMarker agenda is not offered as an overwrite: it would only clear the platform's.
    if (bigMarker.length === 0) {
      showInfo('No agenda in BigMarker', 'This conference has no agenda in BigMarker, so there is nothing to pull.')
      return
    }
    if (JSON.stringify(platform) === JSON.stringify(bigMarker)) {
      showInfo('Agenda already matches', 'The agenda here is the same as in BigMarker. Nothing changed.')
      return
    }
    setComparison({ platform, bigMarker })
  }

  const overwrite = async () => {
    if (!comparison) return
    setOverwriting(true)
    const res = await websApi.updateAgenda(web.WorkspaceId, web.Id, web.ETag, comparison.bigMarker)
    setOverwriting(false)
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      setComparison(null)
      setJustUpdated(true)
    } else if (res.status !== 422) {
      showError('Overwrite failed', res.error || 'Unable to update the agenda')
    }
  }

  return {
    pulling,
    overwriting,
    comparison,
    justUpdated,
    pull,
    overwrite,
    cancel: () => setComparison(null),
    dismissUpdated: () => setJustUpdated(false),
  }
}
